import type { CampaignSheetMode } from '@/@types/api-v1/campaign'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { useNotificationBadge } from '@/components/Functional/hooks/useNotificationBadge'
import { buildDisplayRewards } from '@/components/Functional/utils/rewardGrouping'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { LanguageDialog } from '@/components/Layout/LanguageDialog'
import { RaundLifeCycleDebug } from '@/components/Layout/debug/RaundLifeCycleDebug'
import { CampaignComponents } from '@/components/Pages/home/components/CampaignComponents'
import { DialogCampaignDescription } from '@/components/Pages/home/components/DialogCampaignDescription'
import { DialogCampaignHowToPlay } from '@/components/Pages/home/components/DialogCampaignHowToPlay'
import { DialogFirstLoginConfirm } from '@/components/Pages/home/components/DialogFirstLoginConfirm'
import { DialogPointHistory, type PointHistoryEntry } from '@/components/Pages/home/components/DialogPointHistory'
import { DialogStampDetail } from '@/components/Pages/home/components/DialogStampDetail'
import { PointRallyCourseCard } from '@/components/Pages/home/components/PointRallyCourseCard'
import { StampSheet } from '@/components/Pages/home/components/StampSheet'
import { StampSheetSkeleton } from '@/components/Pages/home/components/StampSheetSkeleton'
import { RewardList } from '@/components/Pages/rewards/components/list/RewardList'
import { RewardListSkeleton } from '@/components/Pages/rewards/components/list/RewardListSkeleton'
import { Button, Image, Skeleton } from '@/components/UI'
import { Tabs } from '@/components/UI/Tabs'
import { Typography } from '@/components/UI/Typography'
import type { SheetData, SpotInStampData, StampInSheetData, UserSheetData, UserSheetHistoryData } from '@/schemas/v2'
import type { CampaignData } from '@/schemas/v2/campaign'
import type { PopupItemData } from '@/schemas/v2/popup'
import { useRouter } from 'next/router'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useHomeStateMachine } from './useHomeStateMachine'

const isUserSheetData = (sheet: UserSheetData | SheetData): sheet is UserSheetData =>
  'history' in sheet && Array.isArray((sheet as UserSheetData).history)

/** ISO 日付文字列を `yyyy/MM/dd HH:mm` 形式にフォーマットする */
function formatAcquiredAt(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const yyyy = d.getFullYear()
  const MM = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}/${MM}/${dd} ${hh}:${mm}`
}

/** スタンプ獲得履歴をポイント履歴ダイアログ用アイテムに変換する */
function buildPointHistoryItems(history: UserSheetHistoryData[], stamps: StampInSheetData[]): PointHistoryEntry[] {
  // history.point が null/undefined の場合（旧データ）はマスタ値にフォールバック
  const stampPointMap = new Map(stamps.map((s) => [s.id, s.point]))
  return history.map((h) => ({
    id: h.id,
    label: h.name,
    point: h.point ?? stampPointMap.get(h.stampId) ?? 0,
    acquiredAt: formatAcquiredAt(h.createdAt),
  }))
}

/** mappedSheets の各スタンプの型（マスタデータ＋ユーザー取得状況） */
type StampWithAcquisition = {
  id: number
  name: string
  spotId: number
  spots: SpotInStampData[]
  period: boolean
  point: number
  image: string
  overlayImage: string | null
  isHas: boolean
  hasCount: number
  stampCount: number
  earnedPoints: number
}

/** mappedSheets の各シートの型（マスタデータ＋ユーザー獲得状況） */
type SheetWithAcquisition = {
  id: number
  name: string
  type: 'stamp_rally' | 'point_rally' | 'point_card' | 'overlay' | 'none'
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  isLocked: boolean
  stamps: StampWithAcquisition[]
  history: UserSheetHistoryData[]
}

/** シートの種別・backgroundImage から StampSheet に渡す campaign オブジェクトを生成する */
function buildSheetCampaignMeta(
  sheet: SheetWithAcquisition,
  campaignIsRepeatStamp: boolean | null,
  campaignStampShape: number | null,
): {
  isRepeatStamp: boolean
  stampShape: 0 | 1
  sheetMode: CampaignSheetMode
} {
  return {
    isRepeatStamp: campaignIsRepeatStamp ?? false,
    stampShape: campaignStampShape === 1 ? 1 : 0,
    sheetMode:
      sheet.type === 'overlay'
        ? 'OVERLAY'
        : sheet.type === 'stamp_rally' && sheet.backgroundImage
          ? 'BACKGROUND'
          : 'NORMAL',
  }
}

export function HomePageSkeleton() {
  return (
    <>
      <Header title={'ホーム'} showLanguage={false} onNotification={() => {}} />
      <main className="pb-[72px]">
        <CampaignComponents
          image={''}
          period={true}
          isDisplayEventPeriod={false}
          viewStartAt={undefined}
          viewEndAt={undefined}
          showCampaignDescription={true}
          isLoading={true}
          onClickSummary={() => {}}
          onClickHowToPlay={() => {}}
        />
        <div className="mt-4 px-4">
          <div className="flex gap-4 border-b border-gray-200 pb-2">
            <Skeleton variant="text" width={90} height={20} />
            <Skeleton variant="text" width={90} height={20} />
          </div>
          <StampSheetSkeleton />
        </div>
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>
      <BottomNavigation activeItem="home" />
    </>
  )
}

type HomePageInnerProps = {
  isAuthenticated: boolean
  campaignData: CampaignData | null
  isShowingConfirm: boolean
  homePopupItems: PopupItemData[]
  onConfirmClose: (skipNext?: boolean) => void
}

export function HomePageInner({
  isAuthenticated,
  campaignData,
  isShowingConfirm,
  homePopupItems,
  onConfirmClose,
}: HomePageInnerProps) {
  const router = useRouter()
  const { campaignSlug, gen } = useCampaignSlug()
  const [activeTab, setActiveTab] = useState(0)
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)

  const { hasUnread } = useNotificationBadge(isAuthenticated)

  const {
    state,
    isLoading,
    isError,
    isSummaryOpen,
    isHowToPlayOpen,
    isStampDetailOpen,
    isPointHistoryOpen,
    selectedStamp,
    sheetsData,
    rewardsData,
    pointHistoryItems,
    send: pageSend,
  } = useHomeStateMachine({
    input: {
      isAuthenticated,
    },
  })

  const [stampSheetImageSizes, setStampSheetImageSizes] = useState<{ width: number; height: number; index: number }[]>(
    [],
  )

  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const prevRef = useRef({
    isTokenRefreshWaiting,
    isWaitingForRefresh: state.matches('waitingForRefresh'),
  })
  useEffect(() => {
    const prev = prevRef.current
    const isWaitingForRefresh = state.matches('waitingForRefresh')
    const current = { isTokenRefreshWaiting, isWaitingForRefresh }
    prevRef.current = current

    const tokenJustEnteredWaiting = current.isTokenRefreshWaiting && !prev.isTokenRefreshWaiting
    const stateJustEnteredWaiting = current.isWaitingForRefresh && !prev.isWaitingForRefresh

    if (
      current.isWaitingForRefresh &&
      current.isTokenRefreshWaiting &&
      (tokenJustEnteredWaiting || stateJustEnteredWaiting)
    ) {
      pageSend({ type: 'REFRESH_COMPLETE' })
    }
  }, [isTokenRefreshWaiting, state, pageSend])

  const mappedSheets: SheetWithAcquisition[] = (sheetsData?.sheets ?? []).map((sheet) => {
    const userSheet = isUserSheetData(sheet) ? sheet : null
    const history = userSheet?.history ?? []
    const historyMap = new Map(history.map((h) => [h.stampId, h]))

    const sheetHasCount =
      sheet.type === 'stamp_rally'
        ? (userSheet?.meta?.acquiredStampCount ?? 0)
        : sheet.type === 'point_card'
          ? (userSheet?.meta?.totalStampCount ?? 0)
          : 0

    const earnedPoints = sheet.type === 'point_rally' ? (userSheet?.meta?.totalPoint ?? 0) : 0

    return {
      id: sheet.id,
      name: sheet.name,
      type: sheet.type,
      description: '',
      earnedPoints,
      startAt: sheet.startAt ?? '',
      endAt: '',
      total: sheet.totalCount,
      hasCount: sheetHasCount,
      backgroundImage: sheet.backgroundImage ?? '',
      isLocked: sheet.isLocked ?? false,
      stamps: sheet.stamps.map((stamp) => {
        const historyEntry = historyMap.get(stamp.id)
        return {
          id: stamp.id,
          name: stamp.name,
          spotId: stamp.spots?.[0]?.id ?? 0,
          spots: stamp.spots ?? [],
          period: true,
          point: stamp.point,
          image: historyEntry?.image ?? stamp.image ?? '',
          overlayImage: historyEntry?.overlayImage ?? null,
          isHas: (historyEntry?.acquiredCount ?? 0) >= 1,
          hasCount: historyEntry?.acquiredCount ?? 0,
          stampCount: historyEntry?.acquiredCount ?? 0,
          earnedPoints: 0,
        }
      }),
      history,
    }
  })

  const handleCheckin = (spotId: number) => {
    router.push({ pathname: gen('/spots/detail'), query: { id: spotId } })
  }

  // スタンプラリータブに表示するシート（stamp_rally / point_card / overlay）
  const stampRallySheets = mappedSheets.filter(
    (s) => s.type === 'stamp_rally' || s.type === 'point_card' || s.type === 'overlay',
  )

  // ポイントラリータブに表示するシート（point_rally のみ）
  const pointRallySheets = mappedSheets.filter((s) => s.type === 'point_rally')

  // point_rally シートが 0 件の場合はタブ 0 に強制（派生値）
  const effectiveActiveTab = pointRallySheets.length === 0 ? 0 : activeTab

  // 両タイプが存在する場合のみタブを表示する
  const showTabs = stampRallySheets.length > 0 && pointRallySheets.length > 0

  const displayRewards = useMemo(() => buildDisplayRewards(rewardsData?.rewards ?? []), [rewardsData])

  const campaignBgImage = campaignData?.backgroundImage ?? null
  const mainBackgroundStyle: CSSProperties = campaignBgImage
    ? {
        backgroundImage: `url(${campaignBgImage})`,
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
      }
    : {}

  const hasMultipleLanguages = (campaignData?.language.length ?? 0) > 1
  const showLanguageButton = hasMultipleLanguages && (campaignData?.isLocalize ?? hasMultipleLanguages)

  return (
    <>
      <Header
        title={'ホーム'}
        showLanguage={showLanguageButton}
        onLanguage={() => setIsLanguageDialogOpen(true)}
        onNotification={() => router.push(gen('/news'))}
        hasUnreadBadge={hasUnread}
      />
      <LanguageDialog
        isOpen={isLanguageDialogOpen}
        onClose={() => setIsLanguageDialogOpen(false)}
        languages={campaignData?.language ?? []}
      />
      <main className="pb-[72px]" style={mainBackgroundStyle}>
        <CampaignComponents
          image={campaignData?.image ?? ''}
          viewStartAt={campaignData?.viewStartAt}
          viewEndAt={campaignData?.viewEndAt}
          period={true}
          isDisplayEventPeriod={campaignData?.isDisplayEventPeriod ?? false}
          showCampaignDescription={true}
          isLoading={isLoading}
          onClickSummary={() => pageSend({ type: 'OPEN_SUMMARY' })}
          onClickHowToPlay={() => pageSend({ type: 'OPEN_HOW_TO_PLAY' })}
        />
        {campaignData?.homeExpansionTopImage && (
          <div className="mt-4" data-testid="home-expansion-top-image">
            <Image src={campaignData.homeExpansionTopImage} fit="width-fit" />
          </div>
        )}
        {!isAuthenticated && (
          <div className="flex justify-center mt-4 px-4">
            <Button
              label="参加する"
              fullWidth
              size={'sm'}
              onClick={() => {
                location.href = `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}`
              }}
            />
          </div>
        )}
        <div className="mt-4">
          {isLoading ? (
            <div className="px-4">
              <div className="flex gap-4 border-b border-gray-200 pb-2">
                <Skeleton variant="text" width={90} height={20} />
                <Skeleton variant="text" width={90} height={20} />
              </div>
              <StampSheetSkeleton />
            </div>
          ) : isError ? (
            <div className="px-4 py-8 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-gray-600">データの取得に失敗しました</p>
              <button
                type="button"
                className="px-4 py-2 bg-main text-white rounded-full text-sm"
                onClick={() => pageSend({ type: 'RETRY' })}
              >
                再試行
              </button>
            </div>
          ) : (
            <>
              {showTabs && (
                <Tabs
                  tabs={['スタンプラリー', 'ポイントラリー']}
                  activeIndex={effectiveActiveTab}
                  onChange={setActiveTab}
                />
              )}
              {(showTabs ? effectiveActiveTab === 0 : stampRallySheets.length > 0) && (
                <div className="px-4">
                  {stampRallySheets.length > 1 ? (
                    <>
                      <div id="stamp-sheet-pagination" className="flex justify-center pb-2 pt-4" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#stamp-sheet-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                        autoHeight
                      >
                        {stampRallySheets.map((sheet, i) => (
                          <SwiperSlide key={sheet.id}>
                            <StampSheet
                              idx={i}
                              sheet={sheet}
                              campaign={buildSheetCampaignMeta(
                                sheet,
                                campaignData?.isRepeatStamp ?? null,
                                campaignData?.stampShape ?? null,
                              )}
                              isLocked={sheet.isLocked}
                              startAt={sheet.startAt}
                              onStampClick={(stamp) => {
                                if (!sheet.isLocked) pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })
                              }}
                              setStampSheetImageSize={setStampSheetImageSizes}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : stampRallySheets.length === 1 ? (
                    (() => {
                      const sheet = stampRallySheets[0]
                      return (
                        <div className="pt-4">
                          <StampSheet
                            idx={0}
                            sheet={sheet}
                            campaign={buildSheetCampaignMeta(
                              sheet,
                              campaignData?.isRepeatStamp ?? null,
                              campaignData?.stampShape ?? null,
                            )}
                            isLocked={sheet.isLocked}
                            startAt={sheet.startAt}
                            onStampClick={(stamp) => {
                              if (!sheet.isLocked) pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })
                            }}
                            setStampSheetImageSize={setStampSheetImageSizes}
                          />
                        </div>
                      )
                    })()
                  ) : null}
                </div>
              )}
              {campaignData?.homeExpansionBottomImage && (
                <div className="mt-4" data-testid="home-expansion-bottom-image">
                  <Image src={campaignData.homeExpansionBottomImage} fit="width-fit" />
                </div>
              )}
              {(showTabs ? effectiveActiveTab === 1 : pointRallySheets.length > 0) && (
                <div className="px-4 pt-4">
                  {pointRallySheets.length > 1 ? (
                    <>
                      <div id="point-rally-pagination" className="flex justify-center pb-2" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#point-rally-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                      >
                        {pointRallySheets.map((sheet) => (
                          <SwiperSlide key={sheet.id}>
                            <PointRallyCourseCard
                              courseName={sheet.name}
                              earnedPoints={sheet.earnedPoints}
                              onClickHistory={() =>
                                pageSend({
                                  type: 'OPEN_POINT_HISTORY',
                                  items: buildPointHistoryItems(sheet.history, sheet.stamps),
                                })
                              }
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : (
                    <PointRallyCourseCard
                      courseName={pointRallySheets[0].name}
                      earnedPoints={pointRallySheets[0].earnedPoints}
                      onClickHistory={() =>
                        pageSend({
                          type: 'OPEN_POINT_HISTORY',
                          items: buildPointHistoryItems(pointRallySheets[0].history, pointRallySheets[0].stamps),
                        })
                      }
                    />
                  )}
                </div>
              )}
            </>
          )}
          {campaignData?.homeExpansionBottomImage && (
            <div className="mt-4">
              <img
                src={campaignData.homeExpansionBottomImage}
                alt=""
                className="w-full"
                data-testid="home-expansion-bottom-image"
              />
            </div>
          )}
        </div>
        {/* 特典一覧 */}
        {(rewardsData?.rewards?.length ?? 0) > 0 && (
          <div className="mt-6">
            <Typography variant="h3" className="px-4 mb-2">
              特典
            </Typography>
            {isLoading ? <RewardListSkeleton /> : <RewardList rewards={displayRewards} />}
          </div>
        )}
        {/* 以下のダイアログはcampaignDataが読み込まれた後で表示されるので、||を使って問題ない */}
        <DialogCampaignDescription
          title={campaignData?.name || ''}
          description={campaignData?.description || ''}
          isOpen={isSummaryOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogCampaignHowToPlay
          usage={campaignData?.usageHtml || ''}
          isOpen={isHowToPlayOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogStampDetail
          stamp={selectedStamp}
          isOpen={isStampDetailOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
          onCheckin={handleCheckin}
          stampShape={campaignData?.stampShape === 1 ? 1 : 0}
        />
        <DialogFirstLoginConfirm
          images={homePopupItems.map((p) => p.image).filter((img): img is string => img !== null)}
          campaignName={campaignData?.name || ''}
          isOpen={isShowingConfirm}
          onClose={() => onConfirmClose()}
          onConfirm={(skipNext) => onConfirmClose(skipNext)}
        />
        <DialogPointHistory
          items={pointHistoryItems}
          isOpen={isPointHistoryOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>
      <BottomNavigation activeItem="home" />
    </>
  )
}
